import React, { useCallback, useMemo } from 'react';
import { useSwitch, useTheme } from '@/hooks';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import { IconEmojiPicker } from '@/assets/editor';
import cx from 'clsx';
import style from './index.module.scss';
import { devLog } from '@/utils';
import { configByCategory } from './const';
import { CommonDependency } from '@/dependency';
import { isMac } from '@/consts';
import { Popover, PopoverProps } from 'react-tiny-popover';

export const EditorEmojiPicker = ({
  onEmojiSelect,
  viewportOverlay = '.ReactModal__Overlay', // FIXME: hard coding until we introduce a better popover component
  positions,
}: {
  onEmojiSelect: (emoji: string) => void;
  viewportOverlay?: string;
  positions?: PopoverProps['positions'];
}) => {
  const { isDark } = useTheme();
  const { on: isActive, turnOff, toggle } = useSwitch();
  const onEmojiClick = useCallback(
    emojiItem => {
      turnOff();
      devLog(emojiItem);
      if (emojiItem?.emoji) {
        onEmojiSelect(emojiItem?.emoji);
      }
    },
    [onEmojiSelect, turnOff],
  );

  const triggerContainer = useMemo(
    () => (
      <div className={style.iconWrapper} onClick={toggle}>
        <IconEmojiPicker
          className={cx(style.icon, isActive && style.isActive)}
          width={20}
          height={20}
        />
      </div>
    ),
    [isActive, toggle],
  );

  const content = useMemo(
    () => (
      <EmojiPicker
        skinTonesDisabled
        theme={isDark ? Theme.DARK : Theme.LIGHT}
        width={410}
        height={320}
        searchPlaceHolder="Search Emoji"
        lazyLoadEmojis={true}
        onEmojiClick={onEmojiClick}
        categories={configByCategory}
        emojiStyle={
          CommonDependency.richtextEditorConfig?.emojiStyle || isMac
            ? EmojiStyle.NATIVE
            : EmojiStyle.APPLE
        }
        previewConfig={{
          showPreview: false,
        }}
      />
    ),
    [isDark, onEmojiClick],
  );

  return (
    <Popover
      isOpen={isActive}
      onClickOutside={turnOff}
      positions={positions || ['bottom', 'top', 'right', 'left']}
      align="start"
      reposition
      containerClassName="emojiPickerOverlay"
      boundaryElement={
        (viewportOverlay && (document?.body?.querySelector(viewportOverlay) as HTMLElement)) ||
        document?.body
      }
      padding={10}
      content={content}>
      {triggerContainer}
    </Popover>
  );
};
